import React from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeftLong } from '@fortawesome/free-solid-svg-icons';
import {
  borderRadius,
  delay,
  font,
  gutter,
  inputheight,
} from '@/views/styles/variables';
import i18next from 'i18next';

const buttons = [
  'primary',
  'warning',
  'danger',
  'success',
  'secondary',
  'inactive',
  'green-dark',
  'blue-primary',
  'white-primary',
  'back'
];

interface ButtonInterface {
  $color?:
    | 'primary'
    | 'warning'
    | 'danger'
    | 'success'
    | 'secondary'
    | 'inactive'
    | 'green-dark'
    | 'blue-primary'
    | 'white-primary'
    | 'back';

  onClick?: any;
  $size?: 'xs' | 'small' | 'default' | 'large';
  $fullWidth?: boolean;
  $gutter?: 'top' | 'bottom';
  $outline?: boolean;
  $withIcon?: boolean;
  $borderRadius?: boolean;
  type?: string;
  $withLabel?: boolean;
  $withLogin?: boolean;
  $link?: boolean;
  $transparent?: boolean;
  $disabled?: boolean;
  $iconOnly?: boolean;
  $withShadow?: boolean;
  'data-testid'?: string;
  $textOnly?: boolean;
}

export const Button = styled.button.attrs<ButtonInterface>((props) => ({
  'data-testid': props['data-testid'] || 'button',
  disabled:
    props.disabled ||
    (props.$color === 'inactive' && props.$disabled === false)
}))<ButtonInterface>`
  display: inline-block;
  background-color: transparent;
  padding: 0.5rem 1.5rem;
  height: ${inputheight.default};
  font-size: ${font.size.default};
  font-weight: ${font.weight.semibold};
  line-height: 1.43;
  border: 1px solid transparent;
  border-radius: ${borderRadius.large};
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transition: ${delay};
  cursor: pointer;

  &:focus,
  &:active {
    outline: none;
    box-shadow: none;
  }

  &:disabled {
    cursor: not-allowed;
    box-shadow: none;

    ${(props) =>
      props.$color === 'inactive'
        ? css`
            opacity: 0.5;
            color: ${({ theme }) => theme.colors.inactive.text};
            background-color: ${({ theme }) => theme.colors.inactive.default};
            border: 1px solid transparent;

            &:hover {
              color: ${({ theme }) => theme.colors.inactive.text};
              background-color: ${({ theme }) => theme.colors.inactive.default};
              border: 1px solid transparent;
            }
          `
        : css`
            opacity: 0.5;
            background-color: #dfdfdf;
            border: 1px solid #585858;
            color: #585858;

            &:hover {
              background-color: #dfdfdf;
              border: 1px solid #585858;
              color: #585858;
            }
      `}
  }

  .icon {
    vertical-align: middle;
    padding: 0px;
    margin-right: ${gutter.xs};
    font-size: inherit;
  }

  ${buttons.map(
    (button) => (props: ButtonInterface) =>
      props.$color === button &&
      css`
        color: ${({ theme }) => theme.colors[button].text};
        background-color: ${({ theme }) => theme.colors[button].default};
        &:hover {
          background: ${({ theme }) => theme.colors[button].hover};
        }
        &:active {
          opacity: 0.7;
          /*background-color: ${({ theme }) => theme.colors[button].text};*/
        }
      `
  )}

  ${(props) =>
    props.$link &&
    css`
      color: ${({ theme }) => theme.link.color};
    `}

  ${(props) =>
    props.$withShadow &&
    css`
      box-shadow:
        0px 2px 4px -2px rgba(24, 39, 75, 0.12),
        0px 4px 4px -2px rgba(24, 39, 75, 0.08);
    `}

  ${(props) =>
    props.$withShadow &&
    css`
      box-shadow:
        0px 2px 4px -2px rgba(24, 39, 75, 0.12),
        0px 4px 4px -2px rgba(24, 39, 75, 0.08);
    `}

  ${(props) =>
    props.$size === 'small' &&
    css`
      padding: 8px 12px;
      height: 32px;
      font-size: ${font.size.buttonText.small};
      border-radius: ${borderRadius.large};
    `}

  ${(props) =>
    props.$size === 'large' &&
    css`
      padding: 8px 16px;
      height: 40px;
      font-size: ${font.size.buttonText.medium};
      border-radius: ${borderRadius.large};
    `}

  ${(props) =>
    props.$fullWidth &&
    css`
      width: 100%;
    `}

  ${(props) =>
    props.$gutter === 'top' &&
    css`
      margin-top: ${gutter.default};
    `}

  ${(props) =>
    props.$gutter === 'bottom' &&
    css`
      margin-bottom: ${gutter.default};
    `}

  ${(props) =>
    props.$withIcon &&
    css`
      display: flex;
      align-items: center;
      label {
        display: block;
        margin: 0 auto;
        cursor: pointer;
      }
      i {
        height: ${props.$size === 'small' ? '16px' : '24px'};
        margin: 0 ${props.$size === 'small' ? '8px' : '12px'};
        cursor: pointer;
      }
    `}

  ${(props) =>
    props.$borderRadius === false &&
    css`
      border-radius: 0;
    `}

  ${(props) =>
    props.$withLabel &&
    css`
      margin-top: calc(${gutter.default} + ${gutter.small} + 20px);
    `}

  ${(props) =>
    props.$withLogin &&
    css`
      background: ${({ theme }) => theme.login.background};
      color: ${({ theme }) => theme.login.color};
    `}

  ${buttons.map(
    (button) => (props: ButtonInterface) =>
      props.$color === button &&
      props.$outline &&
      css`
        color: ${({ theme }) => theme.colors[button].default};
        border: 1px solid ${({ theme }) => theme.colors[button].default};
        background-color: ${({ theme }) =>
          props.$transparent ? 'transparent' : theme.colors[button].background};

        &:hover {
          color: ${({ theme }) => theme.colors[button].text};
          background: ${({ theme }) => theme.colors[button].secondary};
        }
      `
  )}
  ${(props) =>
    props.$iconOnly &&
    css`
      padding: 4px;
      border: none;
      background: none;
      cursor: pointer;
      color: ${({ theme }) => theme.text.secondary};
      transition: color 0.2s;
      font-size: ${font.size.default};

      &:hover {
        color: ${({ theme }) => theme.colors.warning.default};
      }
    `}

  ${(props) =>
    props.$textOnly &&
    css`
      padding: 0;
      border: none;
      background: none;
      box-shadow: none;
      cursor: pointer;
      color: ${props.$color
        ? ({ theme }) => theme.colors[props.$color!].default
        : ({ theme }) => theme.text.default};
      display: inline-flex;
      align-items: center;

      &:focus {
        outline: none;
      }

      &:hover {
        color: ${props.$color
          ? ({ theme }) => theme.colors[props.$color!].hover
          : ({ theme }) => theme.text.default};
        background: transparent;
      }
    `}
`;

export const BackButton = (props: any) => (
  <Button {...props} $color="link">
    <FontAwesomeIcon icon={faLeftLong} />
    &nbsp;
    {i18next.t('Back')}
  </Button>
);
